import RenderSvg from "@/components/Svg/RenderSvg";
import { t } from "@/helper/helper";
import styled from "styled-components";
import { Bounce, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ICredit } from "global-types/global.types";
import useCredit from "@/hooks/pages/product/useCredit";
import {
  useState,
  useCallback,
  useRef,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";

interface Props {
  locale: string;
  loanData: Pick<ICredit, "mounth" | "product_id">;
  toggleModal: (val: boolean) => void;
}

const PREFIX = "+994 ";

function formatPhone(digits: string): string {
  const d = digits.slice(0, 9);
  let result = PREFIX;
  if (d.length > 0) result += d.slice(0, 2);
  if (d.length > 2) result += " " + d.slice(2, 5);
  if (d.length > 5) result += "-" + d.slice(5, 7);
  if (d.length > 7) result += "-" + d.slice(7, 9);
  return result;
}

export default function BuyWithLoanModal({
  locale,
  toggleModal,
  loanData,
}: Props) {
  const [digits, setDigits] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const displayValue = digits.length > 0 ? formatPhone(digits) : "";

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const allDigits = raw.replace(/\D/g, "");
    let cleaned = allDigits;
    if (cleaned.startsWith("994") && cleaned.length > 3) {
      cleaned = cleaned.slice(3);
      setIsError(false)
    }
    setDigits(cleaned.slice(0, 9));
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      setDigits((prev) => prev.slice(0, -1));
    }
  }, []);

  const handleFocus = useCallback(() => {
    setTimeout(() => {
      if (inputRef.current) {
        const len = inputRef.current.value.length;
        inputRef.current.setSelectionRange(len, len);
      }
    }, 0);
  }, []);

  const [error, setIsError] = useState(false);

  const { mutatePostCredit } = useCredit();

  const isPhoneComplete = digits.length === 9;

  const handleSubmit = () => {
    if (isPhoneComplete) {
      const phone = "+994" + digits;
      setIsError(false)
      mutatePostCredit(
        {
          ...loanData,
          phone,
        },
        {
          onSuccess: () => {
            toast.success(t("successOperation", locale), {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
              transition: Bounce,
              style: {
                fontSize: 16,
                backgroundColor: "#06be87",
              },
            });

            toggleModal.call(null, false);
          },
        },
      );
    } else {
      setIsError(true);
    }
  };

  return (
    <Wrapper>
      <div className="success-modal-overlay">
        <div className="modal-x">
          <div className="content" style={{ display: "block" }}>
            <div className="top">
              <div className="success">
                <RenderSvg title="success" />
              </div>
              <button className="close-btn" onClick={() => toggleModal(false)}>
                <img
                  src={require("@/assets/img/x.png").default.src}
                  alt="close"
                />
              </button>
            </div>
            <div className="title-text">{t("buyWithLoan", locale)}</div>
            <div className="text">
              <Input
                ref={inputRef}
                placeholder="+994 XX XXX-XX-XX"
                value={displayValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                type="tel"
                inputMode="numeric"
              />
              {error && (
                <ErrorSec>
                  {t("enterPhoneNumber", locale)}
                </ErrorSec>
              )}
            </div>
            <Button onClick={handleSubmit}>{t("buyWithLoan", locale)}</Button>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}


const ErrorSec=styled.p`
  color: #ff4c4c !important;
  margin-top: 5px !important;
`

const Wrapper = styled.div`
  .success-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.3);
    width: 100vw;
    height: 100vh;
    z-index: 999999999999999;

    .modal-x {
      position: fixed !important;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%);
      background: #ffffff;
      box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.12);
      border-radius: 6px;
      width: 53.5rem;
      height: auto;
      padding: 3.6rem;

      .top {
        display: flex;
        margin-bottom: 20px;

        .success {
          width: 100%;
          display: flex;
          justify-content: center;
        }

        .ellipse {
          width: 260px;
          height: 105px;
          margin-left: auto;
          margin-right: auto;
          background: #d0d4de;
          opacity: 0.25;
          border-radius: 52.5px;
        }

        .close-btn {
          width: 28px;
          height: 28px;
          left: 1164px;
          top: 286px;
          border-radius: 3px;
          background: transparent !important;
        }
      }

      .title-text {
        font-family: "Nunito";
        font-style: normal;
        font-weight: normal;
        font-size: 24px;
        color: #1b1c1f;
        text-align: center;
        margin-bottom: 24px;
      }

      p {
        font-style: normal;
        font-weight: 300;
        font-size: 16px;
        line-height: 150%;
        color: #444444;
        padding: 0 0 36px;
        text-align: center;
      }

      .text {
        font-family: "Nunito";
        font-style: normal;
        font-weight: 300;
        font-size: 16px;
        line-height: 16px;
        color: #444444;
        text-align: center;
        margin-bottom: 24px;
      }

      .orders-btn {
        display: block;
        margin-left: auto;
        margin-right: auto;
        width: 23.6rem;
        height: 6rem;
        background: #ff4c4c;
        border-radius: 6px;
        font-style: normal;
        font-weight: normal;
        font-size: 24px;
        line-height: 24px;
        color: #ffffff;
        margin-top: 2.4rem;
      }
    }

    @media screen and (max-width: 767.9px) {
      .modal-x {
        width: 90vw !important;

        .succes {
          svg {
            width: 100px;
            height: 100px;
          }
        }

        .title-text {
          font-size: 20px !important;
          margin-bottom: 16px !important;
        }

        a {
          font-size: 16px !important;
          height: 50px !important;
        }
      }
    }
  }
`;

const Button = styled.button`
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 20px;
  height: 60px;
  color: #ffffff;
  background: #1b1c1f;
  border-radius: 6px;
  width: 100%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-decoration: unset;
`;

const Input = styled.input`
  width: 70%;
  height: 50px;
  border: none;
  border-bottom: 1px solid #222;
  font-size: 18px;
  letter-spacing: 1px;
  text-align: center;
`;
